<!-- src/components/organization/BasePricingConfigForm.vue -->
<!-- Base pricing configuration form component - Reusable for onboarding and editing -->
<!-- This is a "dumb" component with no API logic, only form fields and validation -->
<!-- RELEVANT FILES: nav-team-add-step3-configure.vue, nav-team-manage.vue, organization-config.ts -->

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { Plus, Trash2 } from 'lucide-vue-next';
import { useFieldArray, useForm } from 'vee-validate';

import type { PricingConfig } from '@/types/organization-config';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { pricingConfigSchema } from '@/types/organization-config';

interface Props {
  initialData?: PricingConfig;
  isSubmitting?: boolean;
  showSkip?: boolean;
  submitLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  initialData: () => ({
    membershipTiers: [{ name: '', monthlyPrice: 0, classesPerMonth: 8 }],
    classPacks: [{ name: '', numClasses: 5, price: 0 }],
    introOffer: '',
    addOns: {
      hrmRentalMonthly: undefined,
      hrmPurchase: undefined,
      lateCancelFee: undefined,
    },
  }),
  isSubmitting: false,
  showSkip: false,
  submitLabel: 'Save',
});

const emit = defineEmits<{
  submit: [data: PricingConfig];
  cancel: [];
}>();

// Form setup with validation
const formSchema = toTypedSchema(pricingConfigSchema);

const { handleSubmit, resetForm } = useForm({
  validationSchema: formSchema,
  initialValues: props.initialData,
});

// Watch for prop changes and reset form
watch(
  () => props.initialData,
  newData => {
    if (newData) {
      nextTick(() => {
        resetForm({
          values: newData,
        });
      });
    }
  },
  { deep: true },
);

// Field arrays for dynamic lists
const {
  fields: membershipFields,
  push: pushMembership,
  remove: removeMembership,
} = useFieldArray('membershipTiers');
const {
  fields: classPackFields,
  push: pushClassPack,
  remove: removeClassPack,
} = useFieldArray('classPacks');

// Add new membership tier
function addMembershipTier() {
  pushMembership({ name: '', monthlyPrice: 0, classesPerMonth: 8 });
}

// Add new class pack
function addClassPack() {
  pushClassPack({ name: '', numClasses: 5, price: 0 });
}

// Form submission
const onFormSubmit = handleSubmit(data => {
  emit('submit', data);
});

// Reset form to initial values
function handleReset() {
  resetForm({
    values: props.initialData,
  });
}
</script>

<template>
  <form class="space-y-[12px]" @submit="onFormSubmit">
    <!-- Membership Tiers Section -->
    <div class="space-y-[4px]">
      <div class="flex items-center justify-between">
        <div>
          <h4 class="text-lg font-semibold">Standard Membership Tiers</h4>
          <p class="text-sm text-muted-foreground mt-1">
            Monthly membership options with recurring billing
          </p>
        </div>
        <UiButton type="button" variant="outline" size="sm" @click="addMembershipTier">
          <Plus class="h-4 w-4 mr-1" />
          Add Tier
        </UiButton>
      </div>

      <div class="rounded-lg">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
          <div
            v-for="(field, index) in membershipFields"
            :key="field.key"
            class="space-y-4 border rounded-lg p-3 sm:p-4"
          >
            <div class="flex items-center justify-between">
              <h5 class="text-sm font-semibold">Tier {{ index + 1 }}</h5>
              <UiButton
                v-if="membershipFields.length > 1"
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Delete tier"
                @click="removeMembership(index)"
              >
                <Trash2 class="h-4 w-4 text-red-500" />
              </UiButton>
            </div>

            <FormField v-slot="{ componentField }" :name="`membershipTiers[${index}].name`">
              <FormItem>
                <FormLabel class="text-sm font-medium text-gray-700"> Tier Name </FormLabel>
                <FormControl>
                  <Input v-bind="componentField" placeholder="e.g., Basic" class="h-10 bg-white" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField v-slot="{ componentField }" :name="`membershipTiers[${index}].monthlyPrice`">
              <FormItem>
                <FormLabel class="text-sm font-medium text-gray-700"> Monthly Price </FormLabel>
                <FormControl>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      v-bind="componentField"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      class="h-10 bg-white pl-7"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField
              v-slot="{ componentField }"
              :name="`membershipTiers[${index}].classesPerMonth`"
            >
              <FormItem>
                <FormLabel class="text-sm font-medium text-gray-700"> Classes / Month </FormLabel>
                <FormControl>
                  <div class="space-y-2">
                    <!-- Select dropdown for preset options -->
                    <Select
                      :model-value="
                        [4, 8, 12, 'unlimited'].includes(componentField.modelValue)
                          ? String(componentField.modelValue)
                          : 'custom'
                      "
                      @update:model-value="
                        val => {
                          if (val === 'custom') {
                            componentField['onUpdate:modelValue']?.(5);
                            return;
                          }
                          if (val === 'unlimited') {
                            componentField['onUpdate:modelValue']?.('unlimited');
                          } else {
                            componentField['onUpdate:modelValue']?.(Number(val));
                          }
                        }
                      "
                    >
                      <SelectTrigger class="h-10 bg-white">
                        <SelectValue placeholder="Select or enter custom" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4"> 4 classes </SelectItem>
                        <SelectItem value="8"> 8 classes </SelectItem>
                        <SelectItem value="12"> 12 classes </SelectItem>
                        <SelectItem value="unlimited"> Unlimited </SelectItem>
                        <SelectSeparator />
                        <SelectItem value="custom"> Custom amount... </SelectItem>
                      </SelectContent>
                    </Select>

                    <!-- Custom input field (shown when not using presets) -->
                    <div
                      v-if="![4, 8, 12, 'unlimited'].includes(componentField.modelValue)"
                      class="relative"
                    >
                      <Input
                        :model-value="componentField.modelValue || ''"
                        type="number"
                        min="1"
                        step="1"
                        placeholder="0"
                        class="h-10 bg-white pr-20"
                        @update:model-value="
                          val => {
                            const num = Number(val);
                            if (!Number.isNaN(num) && num > 0) {
                              componentField['onUpdate:modelValue']?.(num);
                            }
                          }
                        "
                      />
                      <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        >classes</span
                      >
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
      </div>
    </div>

    <!-- Class Packs Section -->
    <div class="space-y-[4px]">
      <div class="flex items-center justify-between">
        <div>
          <h4 class="text-lg font-semibold">Class Packs (One-Time Purchase)</h4>
          <p class="text-sm text-muted-foreground mt-1">
            Single purchase packages for flexible attendance
          </p>
        </div>
        <UiButton type="button" variant="outline" size="sm" @click="addClassPack">
          <Plus class="h-4 w-4 mr-1" />
          Add Pack
        </UiButton>
      </div>

      <div>
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
          <div
            v-for="(field, index) in classPackFields"
            :key="field.key"
            class="space-y-4 border rounded-lg p-3 sm:p-4"
          >
            <div class="flex items-center justify-between">
              <h5 class="text-sm font-semibold">Pack {{ index + 1 }}</h5>
              <UiButton
                v-if="classPackFields.length > 1"
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Delete pack"
                @click="removeClassPack(index)"
              >
                <Trash2 class="h-4 w-4 text-red-500" />
              </UiButton>
            </div>

            <FormField v-slot="{ componentField }" :name="`classPacks[${index}].name`">
              <FormItem>
                <FormLabel class="text-sm font-medium text-gray-700"> Pack Name </FormLabel>
                <FormControl>
                  <Input
                    v-bind="componentField"
                    placeholder="e.g., 5-Class Pack"
                    class="h-10 bg-white"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField v-slot="{ componentField }" :name="`classPacks[${index}].numClasses`">
              <FormItem>
                <FormLabel class="text-sm font-medium text-gray-700"> Number of Classes </FormLabel>
                <FormControl>
                  <Input
                    v-bind="componentField"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="5"
                    class="h-10 bg-white"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField v-slot="{ componentField }" :name="`classPacks[${index}].price`">
              <FormItem>
                <FormLabel class="text-sm font-medium text-gray-700"> Price </FormLabel>
                <FormControl>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      v-bind="componentField"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      class="h-10 bg-white pl-7"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
      </div>
    </div>

    <!-- Intro Offer Section -->
    <div class="space-y-3">
      <FormField v-slot="{ componentField }" name="introOffer">
        <FormItem>
          <FormLabel class="text-lg font-semibold"> Intro Offer (New Members) </FormLabel>
          <FormControl>
            <Textarea
              v-bind="componentField"
              placeholder="e.g., 3 classes for $99 within 30 days, or free first class"
              class="resize-none min-h-20 bg-white"
              rows="3"
            />
          </FormControl>
          <FormDescription class="text-sm text-muted-foreground">
            Describe the intro offer for new members. Leave blank if not applicable.
          </FormDescription>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Add-Ons Section -->
    <div class="space-y-4">
      <div>
        <h4 class="text-lg font-semibold">Add-Ons & Additional Fees</h4>
        <p class="text-sm text-muted-foreground mt-1">
          Optional services and fees (leave blank if not applicable)
        </p>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <FormField v-slot="{ componentField }" name="addOns.hrmRentalMonthly">
          <FormItem>
            <FormLabel class="text-sm font-medium whitespace-nowrap text-gray-700">
              HRM Rate Monitor (Purchase)
            </FormLabel>
            <FormControl>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  v-bind="componentField"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  class="h-10 bg-white pl-7"
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="addOns.hrmPurchase">
          <FormItem>
            <FormLabel class="text-sm font-medium text-gray-700 whitespace-nowrap">
              HRM Rate Monitor (Rental/Month)
            </FormLabel>
            <FormControl>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  v-bind="componentField"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  class="h-10 bg-white pl-7"
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="addOns.lateCancelFee">
          <FormItem>
            <FormLabel class="text-sm font-medium text-gray-700"> Late Cancel Fee </FormLabel>
            <FormControl>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  v-bind="componentField"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  class="h-10 bg-white pl-7"
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end items-center gap-3 pt-4 border-t">
      <UiButton
        v-if="showSkip"
        type="button"
        variant="outline"
        :disabled="isSubmitting"
        @click="emit('cancel')"
      >
        Skip for Now
      </UiButton>
      <UiButton
        v-else
        type="button"
        variant="outline"
        :disabled="isSubmitting"
        @click="handleReset"
      >
        Reset
      </UiButton>
      <UiButton type="submit" :loading="isSubmitting" :disabled="isSubmitting">
        {{ submitLabel }}
      </UiButton>
    </div>
  </form>
</template>

<style scoped>
/* Hide number input spinner arrows */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>
